import { ReactElement, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Option, pipe } from 'effect';

import ErrorHandler from './components/ErrorHandler';
import Layout from './components/Layout';
import ParamsNavigate from './components/ParamsNavigate';
import PrivateRoute from './components/Routing/PrivateRoute';
import { getRedirectPathByUser, userHasRoleAccess } from './helpers/AccessManagementHelpers';
import { clearAppDataAndRouteToLogin, getToken, userIsAuthenticated } from './helpers/AuthHelpers';
import { RouteLogger } from './helpers/DebuggingHelpers';
import { authChannel } from './helpers/messages';
import { useAppHistory } from './hooks/useAppHistory';
import { useCanViewSettings } from './hooks/useCanView';
import { useUserIsActiveWatcher } from './hooks/useUserIsActiveWatcher';
import useScanningOverviewProvider from './provider-hooks/useScanningOverviewProvider';
import { useGetLocations } from './services/hooks/useGetLocations';
import { useGetUserQuery } from './services/rtk/AuthAPI';
import { useLazyGetSettingsQuery } from './services/rtk/SettingsAPI';
import { parseAdminOrSiteAdmin, User } from './types/User';
import ErrorPage from './views/ErrorPage';
import Login from './views/Login';
import QualityAssuranceCheck from './views/QualityAssuranceCheck';
import ScanningOverview from './views/ScanningOverview';
import ScanOrBoxDetail from './views/ScanOrBoxDetail/ScanOrBoxDetail';
import ScansHandler from './views/ScansHandler';
import { ScanTypeDetailsView } from './views/ScanTypeDetails/ScanTypeDetailsView';
import { ScanTypesOverview } from './views/ScanTypesOverview/ScanTypesOverview';
import SettingsPage from './views/settings/SettingsPage';
import {
    ADMIN_SETTINGS_ROUTES_ALLOWED_ROLES,
    QA_CHECK_ROUTES_ALLOWED_ROLES,
    SCAN_TYPES_ROUTES_ALLOWED_ROLES,
    SCANNING_ROUTES_ALLOWED_ROLES,
    USER_SETTINGS_KEY
} from './AppConstants';
import addIcons from './Icons';

import 'react-toastify/dist/ReactToastify.css';
import './Global.less';

const SettingsRoute = ({
    currentUser,
    isLoadingUser
}: {
    currentUser?: User;
    isLoadingUser?: boolean;
}): ReactElement => {
    const { canView, isLoading } = useCanViewSettings();
    const isAdmin = userHasRoleAccess(currentUser, ADMIN_SETTINGS_ROUTES_ALLOWED_ROLES);
    return (
        <PrivateRoute
            renderComponent={() => <SettingsPage isAdmin={isAdmin} />}
            allowedRoles={ADMIN_SETTINGS_ROUTES_ALLOWED_ROLES}
            accessCheck={{ canAccess: canView, isLoading }}
            currentUser={currentUser}
            isLoadingUser={isLoadingUser}
        />
    );
};

const App = (): ReactElement => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const isAuthenticated = userIsAuthenticated(getToken());

    const {
        data: currentUser,
        error: errorFetchingUser,
        isLoading: isLoadingUser
    } = useGetUserQuery(undefined, { skip: !isAuthenticated });

    useUserIsActiveWatcher(currentUser);
    useAppHistory();

    const [getSettings, getSettingsResponse] = useLazyGetSettingsQuery();
    const { locations, locationsError } = useGetLocations(isAuthenticated);
    const scanOverviewProps = useScanningOverviewProvider(currentUser);
    const locationNames = useMemo(() => locations?.map((location) => location.name) ?? [], [locations]);

    useEffect(() => {
        addIcons();
        const code = searchParams.get('code');

        if (!userIsAuthenticated(getToken())) {
            if (!sessionStorage.getItem('redirectTo')) {
                sessionStorage.setItem('redirectTo', location.pathname + location.search);
            }

            navigate({ pathname: '/login', search: code ? `?code=${code}` : '' });
        }
    }, []);

    useEffect(() => {
        if (currentUser) {
            getSettings();
        }
    }, [currentUser]);

    useEffect(() => {
        if (errorFetchingUser) {
            navigate('login-error');
        }
    }, [errorFetchingUser]);

    useEffect(() => {
        if (getSettingsResponse.isSuccess) {
            localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(getSettingsResponse.data));
        } else if (getSettingsResponse.isError) {
            ErrorHandler(getSettingsResponse);
        }
    }, [getSettingsResponse]);

    useEffect(() => {
        if (locationsError) {
            if (import.meta.env.VITE_SCANNING_VERSION_SUFFIX !== 'LOCAL') {
                ErrorHandler(undefined, 'Error fetching locations');
            }
        }
    }, [locationsError]);

    useEffect(function listenForLogoutEvent() {
        const handleLogoutEvent = (event: MessageEvent) => {
            if (event.data.type === 'logout') {
                clearAppDataAndRouteToLogin();
            }
        };

        authChannel.addEventListener('message', handleLogoutEvent);

        return () => {
            authChannel.removeEventListener('message', handleLogoutEvent);
        };
    }, []);

    const adminOrSiteAdmin = useMemo(
        () => pipe(currentUser, Option.fromNullable, Option.flatMap(parseAdminOrSiteAdmin)),
        [currentUser]
    );

    return (
        <>
            {/* Logger helper to debug routing changes */}
            <RouteLogger />

            <Routes>
                <Route
                    path="scanning"
                    element={
                        <PrivateRoute
                            renderComponent={() =>
                                scanOverviewProps &&
                                locationNames.length > 0 && (
                                    <ScanningOverview {...scanOverviewProps} destinations={locationNames} />
                                )
                            }
                            currentUser={currentUser}
                            isLoadingUser={isLoadingUser}
                            allowedRoles={SCANNING_ROUTES_ALLOWED_ROLES}
                        />
                    }
                />
                <Route
                    path="scanning/:scanId"
                    element={
                        <PrivateRoute
                            renderComponent={(user) => <ScansHandler currentUser={user} />}
                            currentUser={currentUser}
                            isLoadingUser={isLoadingUser}
                            allowedRoles={SCANNING_ROUTES_ALLOWED_ROLES}
                        />
                    }
                />
                <Route
                    path="scanning/box-scan/:scanId/box/:boxId"
                    element={
                        <PrivateRoute
                            renderComponent={(user) => <ScanOrBoxDetail currentUser={user} />}
                            currentUser={currentUser}
                            isLoadingUser={isLoadingUser}
                            allowedRoles={SCANNING_ROUTES_ALLOWED_ROLES}
                        />
                    }
                />
                <Route
                    path="/arriving-loggers"
                    element={
                        <PrivateRoute
                            renderComponent={() =>
                                scanOverviewProps && (
                                    <ScanningOverview {...scanOverviewProps} destinations={locationNames} />
                                )
                            }
                            currentUser={currentUser}
                            isLoadingUser={isLoadingUser}
                            allowedRoles={SCANNING_ROUTES_ALLOWED_ROLES}
                        />
                    }
                />
                <Route
                    path="/qa-check"
                    element={
                        <PrivateRoute
                            renderComponent={() => <QualityAssuranceCheck />}
                            currentUser={currentUser}
                            isLoadingUser={isLoadingUser}
                            allowedRoles={QA_CHECK_ROUTES_ALLOWED_ROLES}
                        />
                    }
                />

                {currentUser && (
                    <>
                        <Route
                            path="/scans/types"
                            element={
                                <PrivateRoute
                                    renderComponent={() =>
                                        pipe(
                                            adminOrSiteAdmin,
                                            Option.match({
                                                onNone: () => null,
                                                onSome: (user) => <ScanTypesOverview user={user} />
                                            })
                                        )
                                    }
                                    currentUser={currentUser}
                                    isLoadingUser={isLoadingUser}
                                    allowedRoles={SCAN_TYPES_ROUTES_ALLOWED_ROLES}
                                />
                            }
                        />

                        <Route
                            path="/scans/types/:scanTypeId"
                            element={
                                <PrivateRoute
                                    renderComponent={() =>
                                        pipe(
                                            adminOrSiteAdmin,
                                            Option.match({
                                                onNone: () => null,
                                                onSome: (user) => <ScanTypeDetailsView user={user} />
                                            })
                                        )
                                    }
                                    currentUser={currentUser}
                                    isLoadingUser={isLoadingUser}
                                    allowedRoles={SCAN_TYPES_ROUTES_ALLOWED_ROLES}
                                />
                            }
                        />

                        <Route path="settings">
                            <Route index element={<Navigate replace to="my" />} />
                            <Route
                                path="my"
                                element={<SettingsRoute currentUser={currentUser} isLoadingUser={isLoadingUser} />}
                            />
                            <Route
                                path="admin"
                                element={<SettingsRoute currentUser={currentUser} isLoadingUser={isLoadingUser} />}
                            />
                        </Route>

                        <Route path="/" element={<Navigate replace to={getRedirectPathByUser(currentUser)} />} />

                        <Route
                            path="*"
                            element={
                                <Layout
                                    mainContent={<ErrorPage statusCode={404} />}
                                    currentUser={currentUser}
                                    loadingUser={isLoadingUser}
                                />
                            }
                        />
                        <Route
                            path="/no-access"
                            element={
                                <Layout
                                    mainContent={<ErrorPage statusCode={403} />}
                                    currentUser={currentUser}
                                    loadingUser={isLoadingUser}
                                />
                            }
                        />
                    </>
                )}

                <Route path="/login-error" element={<Layout mainContent={<ErrorPage statusCode={401} />} />} />
                <Route path="/login" element={<Login />} />
                <Route path="scan" element={<Navigate replace to="/scanning" />} />
                <Route path="scan/:scanId" element={<ParamsNavigate replace to="/scanning/:scanId" />} />
                <Route path="/scanning/box-scan/:scanId" element={<ParamsNavigate to="/scanning/:scanId" />} />
            </Routes>

            <ToastContainer
                position="top-center"
                autoClose={10000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                limit={5}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

export default App;
